import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/atoms/ProtectedRoute';
import { Spinner } from './components/atoms/Spinner';
import { ChunkErrorBoundary } from './components/atoms/ChunkErrorBoundary';

// Lazy load page components
const MainLayout = lazy(() => import('./components/templates/MainLayout').then(m => ({ default: m.MainLayout })));

// Main pages
const Dashboard = lazy(() => import('./components/pages/Dashboard'));
const Projects = lazy(() => import('./components/pages/Projects'));
const ProjectWorkspace = lazy(() => import('./components/pages/ProjectWorkspace'));
const MissionControl = lazy(() => import('./components/pages/MissionControl'));
const Settings = lazy(() => import('./components/pages/Settings'));
const ProjectSettings = lazy(() => import('./components/pages/ProjectSettings'));
const Integrations = lazy(() => import('./components/pages/Integrations'));
const Connectors = lazy(() => import('./components/pages/Connectors'));
const Profile = lazy(() => import('./components/pages/Profile'));

// Auth pages
const Login = lazy(() => import('./components/pages/Login'));
const Register = lazy(() => import('./components/pages/Register'));

// OAuth callback
const OAuthCallback = lazy(() => import('./components/pages/OAuthCallback'));

// Functional pages
const SeoAudit = lazy(() => import('./components/pages/SeoAudit'));
const AuditDetail = lazy(() => import('./components/pages/AuditDetail'));
const KeywordTracking = lazy(() => import('./components/pages/KeywordTracking'));
const SerpMonitor = lazy(() => import('./components/pages/SerpMonitor'));
const AutomationDashboard = lazy(() => import('./components/pages/AutomationDashboard'));
const Reports = lazy(() => import('./components/pages/Reports'));
const Competitors = lazy(() => import('./components/pages/Competitors'));
const BacklinkProfile = lazy(() => import('./components/pages/BacklinkProfile'));
const Roadmap = lazy(() => import('./components/pages/Roadmap'));

// Phase B-D SEO feature pages
const KeywordMap = lazy(() => import('./components/pages/KeywordMap'));
const KeywordResearch = lazy(() => import('./components/pages/KeywordResearch'));
const ContentOptimization = lazy(() => import('./components/pages/ContentOptimization'));
const Migration = lazy(() => import('./components/pages/Migration'));
const Tasks = lazy(() => import('./components/pages/Tasks'));
const Geo = lazy(() => import('./components/pages/Geo'));
const VisualAnalysis = lazy(() => import('./components/pages/VisualAnalysis'));
const IrysIndex = lazy(() => import('./components/pages/IrysIndex'));

// Content optimization workbench + preset manager
const OptimizationProjectWorkbench = lazy(() => import('./components/pages/OptimizationProjectWorkbench'));
const OptimizationProjectsList = lazy(() => import('./components/pages/OptimizationProjectsList'));
const PresetManager = lazy(() => import('./components/pages/PresetManager'));

// Dashboard pages
const SeoDashboard = lazy(() => import('./components/pages/SeoDashboard'));

// Schedules page
const SeoSchedules = lazy(() => import('./components/pages/SeoSchedules'));

// Marketing feature pages
const AICopilot = lazy(() => import('./components/pages/AICopilot'));
const Workflows = lazy(() => import('./components/pages/Workflows'));
const Social = lazy(() => import('./components/pages/Social'));
const AdsDashboard = lazy(() => import('./components/pages/AdsDashboard'));
const SeaCampaigns = lazy(() => import('./components/pages/SeaCampaigns'));
const MetaAds = lazy(() => import('./components/pages/MetaAds'));
const TikTokAds = lazy(() => import('./components/pages/TikTokAds'));
const CampaignDetail = lazy(() => import('./components/pages/CampaignDetail'));
const SeaPerformance = lazy(() => import('./components/pages/SeaPerformance'));
const SeaKeywords = lazy(() => import('./components/pages/SeaKeywords'));
const NegativeKeywords = lazy(() => import('./components/pages/NegativeKeywords'));
const SearchTerms = lazy(() => import('./components/pages/SearchTerms'));
const AuctionInsights = lazy(() => import('./components/pages/AuctionInsights'));
const ProductManager = lazy(() => import('./components/pages/ProductManager'));
const AudienceManager = lazy(() => import('./components/pages/AudienceManager'));
const BidRules = lazy(() => import('./components/pages/BidRules'));
const Recommendations = lazy(() => import('./components/pages/Recommendations'));
const ABTesting = lazy(() => import('./components/pages/ABTesting'));
const ConversionTracking = lazy(() => import('./components/pages/ConversionTracking'));

// Loading fallback component (W14 — shared Spinner atom)
const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[50vh]">
    <Spinner />
  </div>
);

// Wrap lazy components with Suspense + chunk-error boundary (auto-reloads
// when a stale chunk hash is requested after a deploy)
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </ChunkErrorBoundary>
);

// Coming soon routes now use actual page components with ComingSoonOverlay

// Wrap layout with Suspense + chunk-error boundary
const LayoutWrapper = () => (
  <ChunkErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <MainLayout />
    </Suspense>
  </ChunkErrorBoundary>
);

// Route configuration
export const router = createBrowserRouter([
  // Auth routes (no sidebar)
  {
    path: '/login',
    element: withSuspense(Login),
  },
  {
    path: '/register',
    element: withSuspense(Register),
  },

  // OAuth callback (no layout)
  {
    path: '/oauth/callback',
    element: withSuspense(OAuthCallback),
  },

  // App routes (with sidebar layout) - PROTECTED
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <LayoutWrapper />
      </ProtectedRoute>
    ),
    children: [
      // Main navigation
      { index: true, element: withSuspense(Dashboard) },
      { path: 'projects', element: withSuspense(Projects) },
      { path: 'global-ai', element: withSuspense(AICopilot) },
      { path: 'templates', element: withSuspense(Workflows) },
      { path: 'settings', element: withSuspense(Settings) },
      { path: 'integrations', element: withSuspense(Integrations) },
      { path: 'connectors', element: withSuspense(Connectors) },
      { path: 'profile', element: withSuspense(Profile) },
      { path: 'roadmap', element: withSuspense(Roadmap) },
      { path: 'ab-testing', element: withSuspense(ABTesting) },

      // Project workspace with nested routes
      {
        path: 'projects/:projectId',
        element: withSuspense(ProjectWorkspace),
        children: [
          { index: true, element: withSuspense(MissionControl) },
          { path: 'ai', element: withSuspense(AICopilot) },
          { path: 'workflows', element: withSuspense(Workflows) },
          { path: 'seo', element: withSuspense(SeoAudit) },
          { path: 'seo-dashboard', element: withSuspense(SeoDashboard) },
          { path: 'ads-dashboard', element: withSuspense(AdsDashboard) },
          { path: 'seo/audit/:auditId', element: withSuspense(AuditDetail) },
          { path: 'seo/keywords', element: withSuspense(KeywordTracking) },
          { path: 'seo/serp', element: withSuspense(SerpMonitor) },
          { path: 'seo/backlinks', element: withSuspense(BacklinkProfile) },
          { path: 'seo/competitors', element: withSuspense(Competitors) },
          { path: 'seo/schedules', element: withSuspense(SeoSchedules) },
          { path: 'sea', element: withSuspense(SeaCampaigns) },
          { path: 'sea/meta-ads', element: withSuspense(MetaAds) },
          { path: 'sea/tiktok-ads', element: withSuspense(TikTokAds) },
          { path: 'sea/:campaignId', element: withSuspense(CampaignDetail) },
          { path: 'sea/conversions', element: withSuspense(ConversionTracking) },
          { path: 'sea/performance', element: withSuspense(SeaPerformance) },
          { path: 'sea/keyword-research', element: withSuspense(SeaKeywords) },
          { path: 'sea/negative-keywords', element: withSuspense(NegativeKeywords) },
          { path: 'sea/search-terms', element: withSuspense(SearchTerms) },
          { path: 'sea/auction-insights', element: withSuspense(AuctionInsights) },
          { path: 'sea/products', element: withSuspense(ProductManager) },
          { path: 'sea/audiences', element: withSuspense(AudienceManager) },
          { path: 'sea/bid-rules', element: withSuspense(BidRules) },
          { path: 'sea/recommendations', element: withSuspense(Recommendations) },
          { path: 'social', element: withSuspense(Social) },
          { path: 'reports', element: withSuspense(Reports) },
          { path: 'testing', element: withSuspense(ABTesting) },
          { path: 'settings', element: withSuspense(ProjectSettings) },

          // Phase B-D SEO feature routes
          { path: 'seo/keyword-map', element: withSuspense(KeywordMap) },
          { path: 'seo/keyword-research', element: withSuspense(KeywordResearch) },
          { path: 'seo/content-optimization', element: withSuspense(ContentOptimization) },
          { path: 'seo/content-optimization/projects', element: withSuspense(OptimizationProjectsList) },
          { path: 'seo/content-optimization/optimization-projects/:opId', element: withSuspense(OptimizationProjectWorkbench) },
          { path: 'seo/presets', element: withSuspense(PresetManager) },
          { path: 'seo/migration', element: withSuspense(Migration) },
          { path: 'seo/tasks', element: withSuspense(Tasks) },
          { path: 'seo/geo', element: withSuspense(Geo) },
          { path: 'seo/visual-analysis', element: withSuspense(VisualAnalysis) },
          { path: 'seo/irys-index', element: withSuspense(IrysIndex) },
        ],
      },

      // Legacy routes (kept for backward compatibility)
      { path: 'seo-audit', element: withSuspense(SeoAudit) },
      { path: 'keywords', element: withSuspense(KeywordTracking) },
      { path: 'serp', element: withSuspense(SerpMonitor) },
      { path: 'automation/dashboard', element: withSuspense(AutomationDashboard) },
      { path: 'reports', element: withSuspense(Reports) },
      { path: 'competitors', element: withSuspense(Competitors) },
      { path: 'backlinks', element: withSuspense(BacklinkProfile) },
    ],
  },

  // Catch-all 404
  { path: '*', element: <Navigate to="/" replace /> },
]);

// Route paths for type safety
export const ROUTES = {
  // Main navigation
  HOME: '/',
  DASHBOARD: '/',
  PROJECTS: '/projects',
  GLOBAL_AI: '/global-ai',
  SETTINGS: '/settings',
  INTEGRATIONS: '/integrations',
  CONNECTORS: '/connectors',
  PROFILE: '/profile',
  ROADMAP: '/roadmap',
  TEMPLATES: '/templates',
  AB_TESTING: '/ab-testing',
  LOGIN: '/login',
  REGISTER: '/register',
  OAUTH_CALLBACK: '/oauth/callback',

  // Project workspace routes (use projectId param)
  PROJECT_WORKSPACE: (projectId: string) => `/projects/${projectId}`,
  PROJECT_MISSION_CONTROL: (projectId: string) => `/projects/${projectId}`,
  PROJECT_AI: (projectId: string) => `/projects/${projectId}/ai`,
  PROJECT_WORKFLOWS: (projectId: string) => `/projects/${projectId}/workflows`,
  PROJECT_SEO: (projectId: string) => `/projects/${projectId}/seo`,
  PROJECT_SEO_KEYWORDS: (projectId: string) => `/projects/${projectId}/seo/keywords`,
  PROJECT_SEO_SERP: (projectId: string) => `/projects/${projectId}/seo/serp`,
  PROJECT_SEO_BACKLINKS: (projectId: string) => `/projects/${projectId}/seo/backlinks`,
  PROJECT_SEO_COMPETITORS: (projectId: string) => `/projects/${projectId}/seo/competitors`,
  PROJECT_SEO_SCHEDULES: (projectId: string) => `/projects/${projectId}/seo/schedules`,
  PROJECT_SEA: (projectId: string) => `/projects/${projectId}/sea`,
  PROJECT_SEA_PERFORMANCE: (projectId: string) => `/projects/${projectId}/sea/performance`,
  PROJECT_SEA_KEYWORD_RESEARCH: (projectId: string) => `/projects/${projectId}/sea/keyword-research`,
  PROJECT_SEA_NEGATIVE_KEYWORDS: (projectId: string) => `/projects/${projectId}/sea/negative-keywords`,
  PROJECT_SEA_SEARCH_TERMS: (projectId: string) => `/projects/${projectId}/sea/search-terms`,
  PROJECT_SEA_AUCTION_INSIGHTS: (projectId: string) => `/projects/${projectId}/sea/auction-insights`,
  PROJECT_SEA_PRODUCTS: (projectId: string) => `/projects/${projectId}/sea/products`,
  PROJECT_SEA_AUDIENCES: (projectId: string) => `/projects/${projectId}/sea/audiences`,
  PROJECT_SEA_BID_RULES: (projectId: string) => `/projects/${projectId}/sea/bid-rules`,
  PROJECT_SEA_RECOMMENDATIONS: (projectId: string) => `/projects/${projectId}/sea/recommendations`,
  PROJECT_SOCIAL: (projectId: string) => `/projects/${projectId}/social`,
  PROJECT_TESTING: (projectId: string) => `/projects/${projectId}/testing`,
  PROJECT_SETTINGS: (projectId: string) => `/projects/${projectId}/settings`,
  PROJECT_SEO_DASHBOARD: (projectId: string) => `/projects/${projectId}/seo-dashboard`,
  PROJECT_ADS_DASHBOARD: (projectId: string) => `/projects/${projectId}/ads-dashboard`,
  PROJECT_TEMPLATES: (projectId: string) => `/projects/${projectId}/settings/templates`,

  // Legacy routes (for backward compatibility)
  SEO_AUDIT: '/seo-audit',
  KEYWORDS: '/keywords',
  SERP: '/serp',
  AUTOMATION_DASHBOARD: '/automation/dashboard',
  REPORTS: '/reports',
  COMPETITORS: '/competitors',
  BACKLINKS: '/backlinks',
} as const;
